/**
 * GIWAHS — Admin RPC client
 * ---------------------------------------------------------------------
 * Thin wrapper around the SECURITY DEFINER admin RPCs added by
 * migration 006. All authorization is server-side: each RPC checks
 * is_admin(auth.uid()) and raises 42501 if the caller is not an admin.
 *
 * Lives in /lib/ (not /components/) because dataClient may eventually
 * import these for cross-cutting features (status-change notifications).
 */
import { getSupabaseClient, isSupabaseConfigured } from './supabaseClient';
import { dispatch } from './notifications';
import { track, EVENTS } from './analytics';
import QRCode from 'qrcode';

const sb = () => {
  if (!isSupabaseConfigured()) throw new Error('Supabase not configured.');
  return getSupabaseClient();
};

const _trackingPayload = (rfq, status_label) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://giwahs.com';
  return {
    reference_code: rfq.reference_code,
    status_label,
    tracking_url: `${origin}/track?ref=${encodeURIComponent(rfq.reference_code)}`,
    support_email: 'hello@giwahs.com',
    contact_name:  rfq.buyer_name,
    contact_email: rfq.buyer_email,
    company:       rfq.buyer_company,
    category:      (rfq.product_type || '').split(':')[0]?.trim() || rfq.product_type,
    product_name:  (rfq.product_type || '').split(':')[1]?.trim() || '',
    quantity:      rfq.target_quantity,
    target_price:  rfq.target_unit_price,
    destination_country: rfq.shipping_destination,
    timeline:      rfq.needed_by_date || '',
  };
};

export const adminClient = {
  async isAdmin() {
    try {
      const { data, error } = await sb().rpc('is_admin');
      if (error) throw error;
      return Boolean(data);
    } catch { return false; }
  },

  async listRfqs() {
    const { data, error } = await sb().rpc('admin_list_rfqs');
    if (error) throw error;
    return Array.isArray(data) ? data : [];
  },

  async getRfq(reference) {
    const { data, error } = await sb().rpc('admin_get_rfq', { p_ref: reference });
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    return row || null;
  },

  async assignRfq(reference, assignee) {
    const { error } = await sb().rpc('admin_assign_rfq', { p_ref: reference, p_assignee: assignee || '' });
    if (error) throw error;
    track(EVENTS.RFQ_ASSIGNED, { reference_code: reference, assigned: Boolean(assignee) });
    return { ok: true };
  },

  async addNote(reference, note) {
    const { data, error } = await sb().rpc('admin_add_note', { p_ref: reference, p_note: note });
    if (error) throw error;
    track(EVENTS.RFQ_NOTE_ADDED, { reference_code: reference });
    return { ok: true, event_id: data };
  },

  async advanceStatus(reference, newStatus, note) {
    const { error } = await sb().rpc('admin_advance_status', { p_ref: reference, p_status: newStatus, p_note: note || null });
    if (error) throw error;
    track(EVENTS.RFQ_STATUS_UPDATED, { reference_code: reference, new_status: newStatus });

    // Fire buyer notification email — best-effort, non-blocking.
    try {
      const rfq = await this.getRfq(reference);
      if (rfq && rfq.buyer_email) {
        let qr_data_url = '';
        try {
          const origin = typeof window !== 'undefined' ? window.location.origin : 'https://giwahs.com';
          qr_data_url = await QRCode.toDataURL(`${origin}/track?ref=${encodeURIComponent(reference)}`, {
            errorCorrectionLevel: 'M', margin: 2, width: 300,
            color: { dark: '#0A0A0A', light: '#FFFFFF' },
          });
        } catch { /* QR optional */ }
        dispatch({
          kind: 'rfq_status_update',
          to: rfq.buyer_email,
          payload: { ..._trackingPayload(rfq, _statusLabel(newStatus)), qr_data_url, note: note || '' },
        });
      }
    } catch (e) {
      console.warn('[adminClient] post-advance notification skipped:', e?.message);
    }
    return { ok: true };
  },

  async metrics() {
    const { data, error } = await sb().rpc('admin_metrics');
    if (error) throw error;
    return data || {};
  },

  // ── Network-effect loop helpers ──────────────────────────────────────
  /** Lightweight supplier list for the attach-picker. Uses public read. */
  async listSuppliersForPicker() {
    const { data, error } = await sb()
      .from('suppliers')
      .select('slug, name, country_slug, tier')
      .order('name', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  /** Attach a set of supplier slugs to an RFQ. Server-side admin-gated. */
  async attachSuppliers(reference, slugs) {
    const { error } = await sb().rpc('admin_attach_suppliers_to_rfq', {
      p_ref: reference,
      p_supplier_slugs: slugs,
    });
    if (error) throw error;
    track(EVENTS.RFQ_SUPPLIERS_ATTACHED, { reference_code: reference, count: slugs.length });

    // Fire one supplier_rfq_assigned email per attached supplier — best-effort.
    try {
      const rfq = await this.getRfq(reference);
      if (rfq) {
        const { data: rows } = await sb().rpc('admin_supplier_contacts', { p_slugs: slugs });
        const supplierByEmail = (rows || []).filter((s) => s.contact_email);
        for (const s of supplierByEmail) {
          dispatch({
            kind: 'supplier_rfq_assigned',
            to: s.contact_email,
            payload: {
              supplier_name: s.name || s.slug,
              reference_code: rfq.reference_code,
              category: (rfq.product_type || '').split(':')[0]?.trim() || rfq.product_type,
              product_name: (rfq.product_type || '').split(':')[1]?.trim() || '',
              quantity: rfq.target_quantity,
              destination_country: rfq.shipping_destination,
              target_price: rfq.target_unit_price,
              timeline: rfq.needed_by_date || '',
            },
          });
        }
      }
    } catch (e) {
      console.warn('[adminClient] supplier-assigned notify skipped:', e?.message);
    }

    return { ok: true };
  },

  /** List all supplier claims (pending first) for the admin approval queue. */
  async listClaims() {
    const { data, error } = await sb().rpc('admin_list_supplier_claims');
    if (error) throw error;
    return Array.isArray(data) ? data : [];
  },

  /** Approve or reject a supplier claim. Server-side admin-gated. */
  async reviewClaim(claimId, approve, note) {
    const { error } = await sb().rpc('admin_approve_supplier_claim', {
      p_claim_id: claimId, p_approve: !!approve, p_note: note || null,
    });
    if (error) throw error;
    track(EVENTS.SUPPLIER_CLAIM_REVIEWED, { claim_id: claimId, decision: approve ? 'approved' : 'rejected' });
    return { ok: true };
  },

  // ── Supplier applications (Blocker 2 fix) ─────────────────────────
  async listApplications() {
    const { data, error } = await sb().rpc('admin_list_supplier_applications');
    if (error) throw error;
    return data || [];
  },

  async reviewApplication(appId, approve, note) {
    const { data, error } = await sb().rpc('admin_approve_supplier_application', {
      p_app_id: appId, p_approve: !!approve, p_note: note || null,
    });
    if (error) throw error;
    track(approve ? EVENTS.ADMIN_APP_APPROVED : EVENTS.ADMIN_APP_REJECTED, {
      app_id: appId,
      supplier_slug: data?.supplier_slug || null,
      auth_user_linked: !!data?.auth_user_linked,
    });
    // Best-effort welcome / rejection email
    try {
      // (live email flip handles dispatch; mock-mode just logs to console)
      if (approve) {
        dispatch({
          kind: 'supplier_application_confirmation',
          to: data?.contact_email || null, // RPC doesn't echo the email; UI passes it via the supplier_slug join when needed
          payload: { supplier_slug: data?.supplier_slug },
        });
      }
    } catch { /* email is best-effort */ }
    return data;
  },

  // ── Verification queue (P4) ─────────────────────────────────────────────
  async listVerificationRequests({ status = null } = {}) {
    const { data, error } = await sb().rpc('admin_list_verification_requests', { p_status: status });
    if (error) throw error;
    return data || [];
  },

  async getSupplierDocuments(supplier_id) {
    const { data, error } = await sb().rpc('admin_get_supplier_documents', { p_supplier_id: supplier_id });
    if (error) throw error;
    return data || [];
  },

  async signedDocUrl(storage_path, expires_in_seconds = 60) {
    const client = sb();
    const { data, error } = await client.storage.from('supplier-docs').createSignedUrl(storage_path, expires_in_seconds);
    if (error) throw error;
    return data?.signedUrl || null;
  },

  async decideVerification({ request_id, approve, assigned_tier, note, paid }) {
    const { data, error } = await sb().rpc('admin_decide_verification', {
      p_request_id:    request_id,
      p_approve:       approve,
      p_assigned_tier: assigned_tier || null,
      p_note:          note || null,
      p_paid:          typeof paid === 'boolean' ? paid : null,
    });
    if (error) throw error;
    track(approve ? EVENTS.SUPPLIER_VERIFICATION_APPROVED : EVENTS.SUPPLIER_VERIFICATION_REJECTED, {
      request_id, assigned_tier,
    });
    dispatch({
      kind: approve ? 'verification_approved' : 'verification_rejected',
      to: null,
      payload: {
        request_id,
        supplier_id: data?.supplier_id,
        final_tier: data?.final_tier,
        expires_at: data?.expires_at,
        note: note || '',
      },
    });
    return data || { ok: true };
  },

  async revokeVerification({ supplier_id, note }) {
    const { data, error } = await sb().rpc('admin_revoke_verification', {
      p_supplier_id: supplier_id, p_note: note || null,
    });
    if (error) throw error;
    return data || { ok: true };
  },
};

const _statusLabel = (status) => ({
  submitted:              'Submitted',
  in_queue:               'In Queue',
  reviewing_requirements: 'Reviewing Requirements',
  supplier_shortlisting:  'Supplier Shortlisting',
  quotes_requested:       'Quotes Requested',
  quotes_received:        'Quotes Received',
  buyer_review:           'Buyer Review',
  closed:                 'Closed',
}[status] || status);
